import { LitEntity, html, css, renderIf } from '../../utility/lit-entity';
import { sharedStyles } from '../../styles/shared-styles';
import '../../common/bn-action';
import '../../common/bn-icon';
import '../../common/bn-loading';
import '../../common/bn-property';
import '../../common/bn-surface';
import '../../work-orders/actions/request-service-button';
import '../actions/device-snooze-flag-button';
import './device-details';
import './device-flags';
import '../props/device-sensor-type';
import './device-sensor-error-count';
import '../props/device-time-since-last-event';

class DeviceListItem extends LitEntity {
  static get properties() {
    return {
      latestEvent: { type: Object, attribute: false },
      flagname: { type: String },
      detailsviewable: { type: Boolean },
    };
  }

  static get styles() {
    return [sharedStyles, css`
      :host {
        width: 100%;
        opacity: 1;
      }
      .card-container bn-surface {
        max-height: 1000px;
        transition: 0.2s opacity,
                    0.2s max-height,
                    0.2s margin,
                    0.2s padding,
                    0.2s border-width;
      }
      .card-container.hide {
        pointer-events: none;
      }
      .card-container.hide bn-surface {
        opacity: 0;
        max-height: 0;
        padding-top: 0;
        padding-bottom: 0;
        margin-top: 0;
        margin-bottom: 0;
      }
      bn-surface .row {
        display: flex;
        flex-wrap: wrap;
      }
      bn-flash {
        width: 100%;
      }
      .card-container .card-header {
        display: flex;
        margin-bottom: 0;
        padding-bottom: .5em;
        flex-direction: column;
        transition: .2s border-radius;
      }
      .card-header.open {
        border-radius: 8px 8px 0 0;
      }
      .card-container .card-lower {
        display: flex;
        justify-content: space-between;
        flex-direction: column;
        max-height: 0px;
        opacity: 0;
        margin: 0 var(--root-margin);
        padding: 0 1em 0 1em;
        background-color: var(--gray-40);
        pointer-events: none;
        border-top: 0px solid var(--gray-50);
      }
      .card-lower.visible {
        opacity: 1;
        padding: 1em;
        border-width: 1px;
        pointer-events: all;
        max-height: 1000px;
        border-radius: 0 0 8px 8px;
      }
      .card-lower .row {
        display: flex;
        justify-content: space-between;
        flex-wrap: wrap;
      }
      .card-lower .lower-content-row {
        margin-top: .75rem;
      }
      .card-lower .left {
        display: flex;
        gap: 1em;
      }
      .header-left {
        flex-grow: 3;
        display: flex;
        flex-direction: column;
        justify-content: center;
      }
      .bin-name {
        font-weight: bold;
        font-size: 1.1em;
        margin-bottom: .4em;
      }
      .properties {
        display: flex;
        flex-wrap: wrap;
        gap: 1em;
      }
      .actions {
        display: flex;
        flex-grow: 1;
        flex-wrap: wrap;
        align-items: center;
        justify-content: flex-end;
        margin-right: -.25em;
      }
      .actions > * {
        margin: .25em 0 .25em .25em;
      }
      .actions .button bn-icon {
        font-size: .75em
      }
    `];
  }

  render() {
    if (!this.entity || this.loading) {
      return html`
        <bn-surface>
          <bn-loading><bn-loading>
        <bn-surface>
      `;
    }

    const latestEventHref = this.getHref('https://api.binsentry.com/rel/event-latest');
    const flagsHref = this.getHref('https://api.binsentry.com/rel/device-flags');
    const binHref = this.getHref('https://api.binsentry.com/rel/bin');
    const workOrdersHref = this.getHref('https://api.binsentry.com/rel/work-orders');

    return html`
      <lit-entity .href="${latestEventHref}" @update="${this._onLatestEventUpdate}"></lit-entity>
      <div class="card-container">
        <bn-surface class="card-header ${this.detailsviewable ? 'open' : ''}">
          <div class="row bin-name">
            <bin-path-name .href="${binHref}"></bin-path-name>
          </div>
          <div class="row">
            <bn-flash id="status"></bn-flash>
          </div>
          <div class="row">
            <div class="header-left">
              <div class="properties">
                <bn-property stacked label="Device Id">${this.entity.properties.deviceId}</bn-property>
                <bn-property stacked label="Firmware Version">${this.entity.properties.firmwareVersion}</bn-property>
                <bn-property stacked label="Sensor Type"><device-sensor-type .href="${this.href}"></device-sensor-type></bn-property>
                <bn-property stacked label="External Antenna">${this.entity.properties.hasExternalAntenna}</bn-property>
                ${this._renderAdditionalHeaderProperties()}
              </div>

              ${renderIf(flagsHref, html`
                <device-flags .href="${flagsHref}"></device-flags>
              `)}
            </div>

            <div class="actions">
              ${renderIf(this.flagname, html`
                <device-snooze-flag-button
                  .href="${flagsHref}"
                  flagname="${this.flagname}"
                  @togglesnooze="${this._onVisibilityChange}"
                  @error="${this._onToggleError}"
                ></device-snooze-flag-button>

                <request-service-button
                  .href="${workOrdersHref}"
                  @response="${this._onVisibilityChange}"
                ></request-service-button>
              `)}
              <button class="button button--tertiary" @click="${this._toggleDetails}">
                Details <bn-icon name="far fa-chevron-${this.detailsviewable ? 'up' : 'down'}"></bn-icon>
              </button>
            </div>
          </div>
        </bn-surface>

        <bn-surface class="card-lower ${this.detailsviewable ? 'visible' : ''}">
          <div class="row">
            <div class="left">
              <bn-property stacked label="Hardware Version">${this.entity.properties.hardwareVersion}</bn-property>
              <bn-property stacked label="ECN">${this.entity.properties.engineeringChangeNotice}</bn-property>
              <bn-property stacked label="Xbee Version">${this.entity.properties.xbeeVersion}</bn-property>
              ${this._renderAdditionalLowerProperties()}
            </div>
            <div class="right">
              <slot name="lower-links"></slot>
              <a href="/devices/show?deviceId=${encodeURIComponent(this.href)}">View Device</a>
            </div>
          </div>
          <div class="row lower-content-row">
            <slot name="lower-content"></slot>
          </div>
        </bn-surface>
      </div>
    `;
  }

  _renderAdditionalHeaderProperties() {
    switch (this.flagname) {
    case 'dead':
      return html`
          <bn-property stacked label="Last Read">
            <device-time-since-last-event .href="${this.href}"></device-time-since-last-event>
          </bn-property>
        `;
    case 'battery':
      return html`${this._renderBatteryLevel()}`;
    case 'sensor':
      return html`
          <bn-property stacked label="Sensor Errors">
            <device-sensor-error-count .href="${this.href}"></device-sensor-error-count>
          </bn-property>
        `;
    default:
      return html``;
    }
  }

  _renderAdditionalLowerProperties() {
    switch (this.flagname) {
    case 'dead':
      return html`
          ${this._renderBatteryLevel()}
          ${this._renderBatteryTemperature()}
          ${this._renderSensorTemperature()}
        `;
    case 'battery':
      return html`
          ${this._renderBatteryTemperature()}
          ${this._renderSensorTemperature()}
        `;
    case 'sensor':
      return html`
          ${this._renderBatteryLevel()}
          ${this._renderBatteryTemperature()}
          ${this._renderSensorTemperature()}
        `;
    default:
      return html``;
    }
  }

  _renderBatteryLevel() {
    return html`
      ${renderIf(Number.isFinite(this.latestEvent?.properties.batVolt), () => html`
        <bn-property stacked label="Battery Level">${this.latestEvent?.properties.batVolt}V</bn-property>
      `)}
    `;
  }

  _renderBatteryTemperature() {
    return html`
      ${renderIf(Number.isFinite(this.latestEvent?.properties.batteryTemperature), () => html`
        <bn-property stacked label="Battery Temperature">${this.latestEvent?.properties.batteryTemperature} °C</bn-property>
      `)}
    `;
  }

  _renderSensorTemperature() {
    return html`
      ${renderIf(Number.isFinite(this.latestEvent?.properties.sensorTemperature), () => html`
        <bn-property stacked label="Sensor Temperature">${this.latestEvent?.properties.sensorTemperature} °C</bn-property>
      `)}
    `;
  }

  _toggleDetails() {
    this.dispatchEvent(new CustomEvent('detailsviewablechange', { detail: {
      href: this.href,
      detailsviewable: !this.detailsviewable,
    } }));
  }

  _onVisibilityChange() {
    this.dispatchEvent(new CustomEvent('detailsviewablechange', { detail: {
      href: this.href,
      detailsviewable: false,
    } }));

    const container = this.shadowRoot.querySelector('.card-container');
    container.classList.add('hide');
    this.shadowRoot.querySelector('#status').clear();
  }

  _onToggleError(e) {
    const { error } = e;
    const container = this.shadowRoot.querySelector('.card-container');
    container.classList.remove('hide');
    this.shadowRoot.querySelector('#status').critical(error.message);
  }

  _onLatestEventUpdate(e) {
    this.latestEvent = e.detail.entity;
  }
}

window.customElements.define('device-list-item', DeviceListItem);
